/**
 * This provides the component with an api to global state store (persist accross all components).
 */
import { withHandlers, withProps, compose, branch } from 'recompose';
import { withContext } from 'context';
import withSavedState from './withSavedState';
import { rateRequestFromQuote } from 'utilities/rateRequest';
import _ from 'lodash';
import { Mixpanel } from 'utilities/mixpanel';
import logger from 'utilities/logger';

const onChangeFirstName = ({ context }) => event => {
  const firstName = event.target.value ? event.target.value : '';
  context.setState({ firstName });
};

const onChangeLastName = ({ context }) => event => {
  const lastName = event.target.value ? event.target.value : '';
  context.setState({ lastName });
};

const onChangeEmail = ({ context }) => event => {
  const email = event.target.value ? event.target.value : '';
  context.setState({ email });
};

const onChangeAddress = ({ context }) => event => {
  const address = event.address ? event.address : '';
  context.setState({ address });
};

const onChangeProduct = ({ context }) => value => {
  const product = value ? value : '';
  context.setState({ product });
};

const onChangeDob = ({ context }) => event => {
  const dob = event.target.value ? event.target.value : '';
  context.setState({ dob });
};

const onChangeRiskItem = ({ context }) => value => {
  const risk = { item: value, user_image: value && value.stock_image };
  context.setState({ risk });
};

const onChangeRiskVehicle = ({ context }) => value => {
  const risk = { vehicle: value };
  context.setState({ risk });
};

const onChangeRisk = ({ context }) => risk => {
  context.setState({ risk });
};

const onChangeGender = ({ context }) => value => {
  const gender = value ? value : '';
  context.setState({ gender });
};

const onChangeItemDamage = ({ context }) => value => {
  let updateObj = { itemFreeFromDamage: value };
  if (value === 'no' && context.existingDamageRecorded !== 'no')
    updateObj.existingDamageRecorded = true;
  context.setState(updateObj);
  Mixpanel.track('Free From Damage', { Value: updateObj.itemFreeFromDamage });
  if (updateObj.itemFreeFromDamage === 'no') {
    Mixpanel.track('Declined', { Value: 'Damaged/Not New' });
    Mixpanel.people.increment('Phone Quote Declines');
  }
  setTimeout(() => {
    /**
     * Scroll the error into view after giving it enough time to load (hopefully enough).
     * If it wasn't displayed in 100ms then don't attempt to scroll
     */
    const errorElement = document.getElementById('error');
    if (errorElement) {
      errorElement.scrollIntoView();
    }
  }, 100);
};

const onGetCustomer = ({ context }) => value => {
  const customer = value ? value : {};

  // convert dob from back end to front end
  let date_of_birth;
  if (customer.date_of_birth) {
    const parts = customer.date_of_birth.split('T')[0].split('-');
    date_of_birth = parts[2] + '/' + parts[1] + '/' + parts[0];
  }

  // Done loading customer at this point
  customer.loading = false;

  let newState = { customer };

  // Only cause a state update if the field is defined and is actually different to what we have stored for it
  if (customer.gender && customer.gender != context.gender) newState.gender = customer.gender;
  if (customer.first_name && customer.first_name != context.firstName)
    newState.firstName = customer.first_name;
  if (customer.last_name && customer.last_name != context.lastName)
    newState.lastName = customer.last_name;
  if (customer.location && customer.location != context.address)
    newState.address = customer.location;
  if (customer.date_of_birth && date_of_birth != context.dob) newState.dob = date_of_birth;
  if (customer.email_address && customer.email_address != context.email)
    newState.email = customer.email_address;
  if (customer.mobile_number && customer.mobile_number != context.mobile)
    newState.mobile = customer.mobile_number;
  if (customer.pulse_account_active && customer.pulse_account_active != context.pulseAccountActive)
    newState.pulseAccountActive = customer.pulse_account_active;
  if (customer.pulse_account_id && customer.pulse_account_id != context.pulse_account_id)
    newState.pulse_account_id = customer.pulse_account_id;

  logger.log('GlobalState', 'New State:', { newState });
  context.setState(newState);
};

const onChangeDeclaration = ({ context }) => label => {
  if (label == 'Yes') {
    context.setState({ declaration: { yes: true, no: false } });
  } else {
    context.setState({ declaration: { yes: false, no: true } });
  }
};

const onChangeTermsAcknowledgement = ({ context }) => label => {
  context.setState({ checkedTermsAcknowledgement: !context.checkedTermsAcknowledgement });
};

const getFormattedItem = context => {
  if (context && context.item) {
    const { make, family, series } = context.item;
    return `${make} ${family} ${series}`;
  } else return 'phone';
};

const onChangeQuote = ({ context }) => value => {
  const quote = value ? value : undefined;
  context.setState({ quote });
};

const setQuotePromise = ({ context }) => value => {
  const quotePromise = value;
  context.setState({ quotePromise });
};

const onChangeQuoteExcess = ({ context }) => value => {
  const defaultExcess = value ? value : 0;
  context.setState({ defaultExcess });
};

const onChangeOptExtra = ({ context }) => defaultOptExtra => {
  logger.log('GlobalState', 'Default Optional Extra', { defaultOptExtra });
  context.setState({ defaultOptExtra });
};

const onChangeQuoteStartDate = ({ context }) => quoteStartDate => {
  context.setState({ quoteStartDate });
};

const onChangeQuotePremiumLoading = ({ context }) => value => {
  const premiumLoading = value ? value : false;
  context.setState({ premiumLoading });
};

const onPaymentReturn = ({ context }) => bamboraReciept => {
  context.setState({
    paymentReciept: bamboraReciept
  });
};

const onChangeMobile = ({ context }) => event => {
  const mobile = event.target.value ? event.target.value : '';
  context.setState({ mobile });
  Mixpanel.people.set({ $phone: mobile });
};

const updateManyGlobalState = ({ context }) => (newValues, cb) => {
  logger.log('GlobalState', 'Updating many global state', { newValues });
  if (cb) {
    context.setState(newValues, cb());
  } else {
    context.setState(newValues);
  }
};

const updateGlobalState = ({ context }) => (key, value) => {
  logger.log('GlobalState', 'Updating global state', { key, value });
  context.setState({
    [key]: value
  });
};

const updateGlobalStateFromEvent = ({ context }) => (key, event) => {
  const value = event.target.value;
  const obj = {};
  obj[key] = value;
  context.setState(obj);
};

const getEstimatedPremium = () => async props => {
  const { setState } = props.context;

  // Answers can be stored from the previous page they were on using context
  let answers = props.context.answers;

  // Answers can also be taken from localStorage on a page refresh
  if (!answers) answers = props.answers;

  setState({ estimatedPremium: undefined }); // Show loading indicator

  // Use non-default mainDriver if we have one
  let rateBody = rateRequestFromQuote(props.quote, 'New Business');
  // Car usage
  const carUsage = props.carUsage || (answers && answers.carUsage);

  if (carUsage) rateBody.coverage.usage = { days_a_week: carUsage };

  // Money owed
  const moneyOwed = props.moneyOwed || (answers && answers.moneyOwed);
  if (moneyOwed) rateBody.coverage.risk.finance = { money_owed: moneyOwed == 'yes' ? true : false };

  // Additional drivers
  const additionalDrivers = props.additionalDrivers || (answers && answers.additionalDrivers);
  if (additionalDrivers) {
    rateBody.coverage.drivers = [
      ...rateBody.coverage.drivers.filter(d => d.driver_type === 'main'),
      ...additionalDrivers.map(d => {
        const formattedDriver = _.clone(d);
        formattedDriver.driver_type = 'additional';
        return formattedDriver;
      })
    ];
  }

  logger.log('GlobalState', 'Final rate body', { rateBody });
  let res = await props.coverageApi.useLocalToken().rate(rateBody);

  // Final premium
  setState({ estimatedPremium: res && res.coverage ? res.coverage.premium : false });
};

const onChangeLogin = ({ context }) => loggedIn => {
  // Set the state and clear customer object when user changes
  context.setState({ loggedIn, customer: {} });
};

const onChangePath = ({ context }) => value => {
  Mixpanel.track('Selected Channel', { Value: value });
  const path = value ? value : '';
  context.setState({ path });
};

const commitAnswer = ({ answers, onCreateAnswers, context }) => answer => {
  const newAnswers = _.mergeWith(answers, answer, (objValue, srcValue, key, object, source) => {
    if (Array.isArray(srcValue) && !srcValue.length) {
      return [];
    }
  });
  context.setState({ answers });
  onCreateAnswers(newAnswers);
};

const onChangePaymentComplete = ({ context }) => value => {
  context.setState({ paymentComplete: value });
};

const withGlobalStateHandlers = withHandlers({
  onChangeFirstName,
  onChangeLastName,
  onChangeEmail,
  onChangeDob,
  onChangeAddress,
  onChangeProduct,
  onChangeRiskItem,
  onChangeRiskVehicle,
  onChangeRisk,
  onChangeGender,
  onChangeMobile,
  onChangeItemDamage,
  onPaymentReturn,
  onGetCustomer,
  updateManyGlobalState,
  onChangeDeclaration,
  onChangeQuote,
  onChangeQuoteExcess,
  onChangeOptExtra,
  onChangeQuoteStartDate,
  onChangeQuotePremiumLoading,
  updateGlobalState,
  updateGlobalStateFromEvent,
  setQuotePromise,
  onChangeLogin,
  onChangeTermsAcknowledgement,
  getEstimatedPremium,
  onChangePath,
  onChangePaymentComplete,
  commitAnswer
});

const withGlobalStateProps = withProps(({ context, savedRisk }) => {
  return {
    hasGlobalState: true,
    firstName: context ? context.firstName : '',
    lastName: context ? context.lastName : '',
    dob: context ? context.dob : '',
    selectedProduct: context ? context.selectedProduct : '',
    gender: context ? context.gender : '',
    address: context ? context.address : '',
    email: context ? context.email : '',
    mobile: context ? context.mobile : '',
    estimatedPremium: context ? context.estimatedPremium : false,
    risk: context ? context.risk || savedRisk : savedRisk || {}, // NOTE: why are we loading from localStorage here?
    quote: context ? context.quote : {},
    customer: context ? context.customer : {},
    quotePromise: context ? context.quotePromise : undefined,
    itemFreeFromDamage: context ? context.itemFreeFromDamage : '',
    existingDamageRecorded: context ? context.existingDamageRecorded : false,
    defaultExcess: context ? context.defaultExcess : '',
    defaultOptExtra: context ? context.defaultOptExtra : [],
    quoteStartDate: context ? context.quoteStartDate : '',
    declaration: context && context.declaration ? context.declaration : {},
    formattedItem: getFormattedItem(context),
    loggedIn: context ? context.loggedIn : undefined,
    paymentComplete: context ? context.paymentComplete : null,
    checkedTermsAcknowledgement: context ? context.checkedTermsAcknowledgement : false,
    path: context && context.path ? context.path : ''
  };
});

export default compose(
  // Since this gets composed a lot, all over the place, only add things we dont already have
  branch(({ localStorage }) => !localStorage, withSavedState),
  branch(({ context }) => !context, withContext),
  compose(
    withGlobalStateProps,
    withGlobalStateHandlers
  )
);
